import React, { useState } from 'react';
import { Mail, Eye, EyeOff, Send, CheckCircle2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export const SettingsEmail = ({ settings, setSettings, handleSave }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleTestSmtp = async () => {
    setTesting(true);
    try {
      const res = await api.post('/company/test-smtp');
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al probar conexion SMTP');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Notificaciones por Email</h3>
        <p className="text-sm text-slate-500">
          Configura una cuenta de Gmail para enviar notificaciones a tus clientes cuando apruebes o rechaces un pago reportado.
        </p>
      </div>

      {/* SMTP Config */}
      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
          <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
            <Mail className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <p className="font-medium text-slate-900">Gmail SMTP</p>
            <p className="text-xs text-slate-500">Usa tu cuenta de Gmail con una contrasena de aplicacion</p>
          </div>
          {settings.has_smtp && (
            <CheckCircle2 className="w-5 h-5 text-emerald-500 ml-auto" />
          )}
        </div>

        <div>
          <Label>Email de Gmail</Label>
          <Input
            type="email"
            placeholder="tucuenta@gmail.com"
            value={settings.smtp_email || ''}
            onChange={(e) => setSettings(s => ({ ...s, smtp_email: e.target.value }))}
            data-testid="smtp-email-input"
          />
        </div>

        <div>
          <Label>Contrasena de aplicacion</Label>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="xxxx xxxx xxxx xxxx"
              value={settings.smtp_app_password || ''}
              onChange={(e) => setSettings(s => ({ ...s, smtp_app_password: e.target.value }))}
              data-testid="smtp-password-input"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
            Genera una contrasena de aplicacion en
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-0.5">
              myaccount.google.com/apppasswords
              <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>

        <div>
          <Label>Nombre del remitente (opcional)</Label>
          <Input
            placeholder={settings.name || 'Tu Empresa SA'}
            value={settings.smtp_sender_name || ''}
            onChange={(e) => setSettings(s => ({ ...s, smtp_sender_name: e.target.value }))}
            data-testid="smtp-sender-name-input"
          />
          <p className="text-xs text-slate-500 mt-1">Si no se completa, se usa el nombre de la empresa</p>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            onClick={() => handleSave()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="smtp-save-btn"
          >
            Guardar
          </Button>
          <Button
            variant="outline"
            onClick={handleTestSmtp}
            disabled={testing || !settings.smtp_email || !settings.smtp_app_password}
            data-testid="smtp-test-btn"
          >
            {testing ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                Probando...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send className="w-4 h-4" />
                Enviar email de prueba
              </span>
            )}
          </Button>
        </div>
      </Card>

      {/* Custom text */}
      <Card className="p-5 space-y-4">
        <div>
          <h4 className="font-medium text-slate-900 mb-1">Personalizar mensaje</h4>
          <p className="text-xs text-slate-500">Agrega un texto personalizado que se incluye en el email segun el resultado.</p>
        </div>

        <div>
          <Label className="text-emerald-700">Mensaje cuando se APRUEBA el pago</Label>
          <Textarea
            placeholder="Ej: Tu pago fue acreditado correctamente. Gracias por regularizar tu situacion."
            value={settings.email_custom_text_approved || ''}
            onChange={(e) => setSettings(s => ({ ...s, email_custom_text_approved: e.target.value }))}
            rows={2}
            className="mt-1"
            data-testid="email-text-approved"
          />
        </div>

        <div>
          <Label className="text-red-700">Mensaje cuando se RECHAZA el pago</Label>
          <Textarea
            placeholder="Ej: No pudimos validar tu comprobante. Por favor comunicate con nuestra oficina."
            value={settings.email_custom_text_rejected || ''}
            onChange={(e) => setSettings(s => ({ ...s, email_custom_text_rejected: e.target.value }))}
            rows={2}
            className="mt-1"
            data-testid="email-text-rejected"
          />
        </div>

        <Button
          onClick={() => handleSave()}
          className="bg-slate-900 hover:bg-slate-800"
        >
          Guardar mensajes
        </Button>
      </Card>

      {/* How it works */}
      <Card className="p-5 bg-slate-50 border-slate-200">
        <h4 className="font-medium text-slate-800 mb-2">Como funciona</h4>
        <ul className="text-sm text-slate-600 space-y-2">
          <li className="flex items-start gap-2">
            <span className="text-teal-600 font-bold mt-0.5">1.</span>
            Cuando un cliente reporta un pago, ingresa su email de contacto.
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600 font-bold mt-0.5">2.</span>
            Al aprobar o rechazar el reclamo, se envia un email con el resultado, actas y observaciones.
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600 font-bold mt-0.5">3.</span>
            El email incluye el logo de tu empresa, los mensajes personalizados y un link directo al portal del deudor.
          </li>
        </ul>
      </Card>
    </div>
  );
};
